import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type {
    ApiEmptyResponse,
    ApiOrderResponse,
    EvmFullFillCreateOrderListing,
    EvmOrderData,
    EvmOrderParameters,
    OfferListParams
} from '~/service/api/types';
import fallbackApi from '../fallbackApi';
import type { Address, Hash } from 'viem';

export default (axiosInstance: AxiosInstance) => ({
    /**
   * @description get list of nft offers
   *
   * @tags offer
   * @name listOffer
   * @request GET:/offers/list
   * @response `200` `GetOfferListData` success getting order list
   * @response `default` `EmptyResponse` universal response is used for NoContent and AnyError answers, contains field Status as any data answers
   */
    async listOffer(params?: OfferListParams) {
        const fallbackConfig = fallbackApi<ApiOrderResponse<EvmFullFillCreateOrderListing[]>>({
            text: 'Loading offer list',
            description: 'An error occurred while loading offer list',
            visible: 'error',
            exclude: [
                404,
                403
            ],
            fallback: {
                data: [],
                pagination: {
                    page: 0,
                    size: 0,
                    total: 0
                },
                status: {
                    code: 404,
                    message: 'Response fallbackApi get offer list',
                    msgId: '1'
                }
            }
        });
        const config: AxiosRequestConfig = {
            params: {
                ...params
                    ? { ...params }
                    : {}
            },
            ...fallbackConfig
        };

        const res = await axiosInstance.get<ApiOrderResponse<EvmFullFillCreateOrderListing[]>>(
            `/v1/offers/list`,
            config
        );
        return res.data;
    },

    /**
     * @description fill offer
     *
     * @tags offer
     * @name fillOffer
     * @request POST:/offer
     * @response `default` `ApiEmptyResponse` universal response is used for NoContent and AnyError answers, contains field Status as any content answers
     */
    async fillOffer({ parameters, signature, network, protocolAddress, user }: {
        parameters: EvmOrderParameters;
        protocolAddress: Address;
        signature: Hash;
        user: Address;
        network: User['network'];
    }) {
        // todo сделано так, так как наш api принимает другие типы, в отличие от opensea
        const newParams = {
            ...parameters,
            startTime: String(parameters.startTime),
            endTime: String(parameters.endTime),
            offer: parameters.offer.map(item => ({
                ...item,
                identifierOrCriteria: String(item.identifierOrCriteria),
                startAmount: String(item.startAmount),
                endAmount: String(item.endAmount)
            })),
            consideration: parameters.consideration.map(item => ({
                ...item,
                identifierOrCriteria: String(item.identifierOrCriteria),
                startAmount: String(item.startAmount),
                endAmount: String(item.endAmount)
            }))
        };

        const res = await axiosInstance.post<ApiEmptyResponse>(
            `/v1/offer`,
            {
                parameters: newParams,
                protocolAddress,
                signature
            },
            {
                params: {
                    chain: network,
                    user
                }
            }
        );
        return res.data;
    },
    /**
     * @description cancel created offer listing
     *
     * @tags offer
     * @name cancelOffer
     * @request POST:/offer/cancel
     * @secure
     * @response `default` `ApiEmptyResponse` universal response is used for NoContent and AnyError answers, contains field Status as any content answers
     */
    async cancelOffer(hash: Hash) {
        const res = await axiosInstance.post<ApiEmptyResponse>(
            `/v1/offer/cancel`,
            {},
            {
                params: {
                    hash
                },
                withCredentials: true
            }
        );
        return res.data;
    },

    /**
     * @description update offer
     *
     * @tags offer
     * @name updateOffer
     * @request POST:/offer/update
     * @secure
     * @response `default` `ApiEmptyResponse` universal response is used for NoContent and AnyError answers, contains field Status as any content answers
     */
    async updateOffer(offer: EvmOrderData, hash: string) {
        const res = await axiosInstance.post<ApiEmptyResponse>(
            `/v1/offers/update`,
            offer,
            {
                params: {
                    hash
                },
                withCredentials: true
            }
        );
        return res.data;
    }
} as const);
